import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Mail, Bell, Building2, MapPin } from 'lucide-react';
import { API_ENDPOINTS } from '../utils/config';
import { useTranslation } from '../context/TranslationContext';
import LanguageSelector from './LanguageSelector';
import CurrencySelector from './CurrencySelector';
import AddCompanyModal from './AddCompanyModal';
import AdminNotificationModal from './AdminNotificationModal';
import AddressModal from './AddressModal';
import '../styles/Header.css';

interface HeaderProps {
  onLogout: () => void;
  userType: 'user' | 'admin' | 'useradmin' | null;
  onSentEmailsClick?: () => void;
  onNotificationsClick?: () => void;
  onNewRequestClick?: () => void;
}

const Header: React.FC<HeaderProps> = ({ onLogout, userType, onSentEmailsClick, onNotificationsClick, onNewRequestClick }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [isAddCompanyModalOpen, setIsAddCompanyModalOpen] = useState(false);
  const [isAdminNotificationModalOpen, setIsAdminNotificationModalOpen] = useState(false);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch notification count when component mounts
  useEffect(() => {
    if (userType === 'user' || userType === 'admin') {
      fetchNotificationCount();
      // Set up interval to check for new notifications every 30 seconds
      const interval = setInterval(fetchNotificationCount, 30000);
      return () => clearInterval(interval);
    }
  }, [userType]);

  const fetchNotificationCount = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      const endpoint = userType === 'admin' 
        ? API_ENDPOINTS.ADMIN_NOTIFICATIONS
        : API_ENDPOINTS.REVISION_NOTIFICATIONS;

      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      if (data.success) {
        setNotificationCount(data.count || 0);
      }
    } catch (error) {
      console.error('Error fetching notification count:', error);
    }
  };

  const handleUserClick = () => {
    setShowUserDropdown(!showUserDropdown);
  };

  const handleLogoutClick = () => {
    onLogout();
    setShowUserDropdown(false);
  };

  const handleDeleteAccount = () => {
    if (window.confirm(t('common.delete_account_confirmation'))) {
      onLogout();
      setShowUserDropdown(false);
      // TODO: Implement actual account deletion
    }
  };

  const handleNotificationClick = () => {
    if (userType === 'admin') {
      setIsAdminNotificationModalOpen(true);
    } else if (onNotificationsClick) {
      onNotificationsClick();
    }
    // Clear notification count when clicked
    setNotificationCount(0);
  };

  return (
    <>
      <header className="header">
        <div className="header-container">
          <div className="header-left">
            <h1 className="header-title">{t('common.procurement_agent')}</h1>
            <img 
              src="/eczacibasilogo.png" 
              alt="Eczacıbaşı Logo" 
              className="header-company-logo"
            />
          </div>
          <div className="header-right">
            {(userType === 'user' || userType === 'admin') && (
              <button 
                className="header-notification-btn"
                onClick={handleNotificationClick}
                title={t('common.notifications')}
              >
                <Bell className="w-5 h-5" />
                {notificationCount > 0 && (
                  <span className="notification-badge">{notificationCount}</span>
                )}
              </button>
            )}
            {(userType === 'user' || userType === 'admin') && onNewRequestClick && (
              <>
                {/* Desktop version */}
                <button 
                  className="header-new-request-btn desktop-only"
                  onClick={onNewRequestClick}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {t('common.new_request')}
                </button>
                {/* Mobile version - just icon */}
                <button 
                  className="header-new-request-btn mobile-only"
                  onClick={onNewRequestClick}
                  title={t('common.new_request')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </>
            )}
            {userType === 'admin' && (
              <>
                {/* Desktop version */}
                <button 
                  className="header-add-company-btn desktop-only"
                  onClick={() => setIsAddCompanyModalOpen(true)}
                >
                  <Building2 className="w-4 h-4 mr-2" />
                  {t('common.add_company')}
                </button>
                {/* Mobile version - just icon */}
                <button 
                  className="header-add-company-btn mobile-only"
                  onClick={() => setIsAddCompanyModalOpen(true)}
                  title={t('common.add_company')}
                >
                  <Building2 className="w-4 h-4" />
                </button>
              </>
            )}
            {userType === 'admin' && onSentEmailsClick && (
              <>
                {/* Desktop version */}
                <button 
                  className="header-sent-emails-btn desktop-only"
                  onClick={onSentEmailsClick}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  {t('common.sent_emails')}
                </button>
                {/* Mobile version - just icon */}
                <button 
                  className="header-sent-emails-btn mobile-only"
                  onClick={onSentEmailsClick}
                  title={t('common.sent_emails')}
                >
                  <Mail className="w-4 h-4" />
                </button>
              </>
            )}
            <LanguageSelector />
            <div className="header-user" ref={dropdownRef}>
              <div className="user-avatar" onClick={handleUserClick}>
                <div className="avatar-circle">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="8" r="4" fill="#6B7280"/>
                    <path d="M4 20C4 16.6863 7.58172 14 12 14C16.4183 14 20 16.6863 20 20" fill="#6B7280"/>
                  </svg>
                </div>
                <div className="user-info desktop-only">
                  <span className="user-name">{user?.name || (userType === 'admin' ? t('common.admin_user') : t('common.regular_user'))}</span>
                  <span className="user-role">
                    {userType === 'admin' ? t('common.administrator') : t('common.user')}
                    {user?.company && (
                      <>
                        <span className="bullet">•</span>
                        {user.company.name}
                      </>
                    )}
                  </span>
                </div>
              </div>
              <svg className="dropdown-arrow desktop-only" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 4.5L6 7.5L9 4.5" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              
              {showUserDropdown && (
                <div className="user-dropdown">
                  <div className="dropdown-currency-section">
                    <span className="dropdown-section-title">{t('common.select_currency')}</span>
                    <CurrencySelector isInDropdown={true} />
                  </div>
                  <div className="dropdown-divider"></div>
                  <button className="dropdown-item" onClick={() => setIsAddressModalOpen(true)}>
                    <MapPin className="w-4 h-4" />
                    {t('common.manage_addresses')}
                  </button>
                  <button className="dropdown-item" onClick={handleLogoutClick}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 21H5A2 2 0 0 1 3 19V5A2 2 0 0 1 5 3H9M16 17L21 12M21 12L16 7M21 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {t('common.logout')}
                  </button>
                  <button className="dropdown-item delete-account" onClick={handleDeleteAccount}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 6H5H21M8 6V4A2 2 0 0 1 10 2H14A2 2 0 0 1 16 4V6M19 6V20A2 2 0 0 1 5 20V6H19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {t('common.delete_account')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <AddCompanyModal
        isOpen={isAddCompanyModalOpen}
        onClose={() => setIsAddCompanyModalOpen(false)}
        onCompanyAdded={(company) => {
          console.log('Company added:', company);
          // Optionally refresh data or show success message
        }}
      />
      
      <AdminNotificationModal
        isOpen={isAdminNotificationModalOpen}
        onClose={() => setIsAdminNotificationModalOpen(false)}
      />
      
      <AddressModal
        isOpen={isAddressModalOpen}
        onClose={() => setIsAddressModalOpen(false)}
      />
      
    </>
  );
};

export default Header; 